/* eslint-disable no-unused-vars */
import {
    Box,
    Divider,
    Flex,
    Heading,
    HStack,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { OrderSection } from './dashboard/components/DashboardStyles';
import {
    fetchOrderByID,
    fetchSingleOrder,
    initiateProductReturn,
} from 'redux/actions/orderActions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import moment from 'moment';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';
import { SingleProductReturnModal } from 'components/Dashboard/ReturnModal';
import { checkReturnable } from 'utils/utils';
import useGetUserDetails from 'hooks/useGetUserDetail';
import { isEmpty } from 'lodash';

const OrderDetail = () => {
    let { id } = useParams();
    const dispatch = useDispatch();
    const user = useGetUserDetails();
    const [returnModalOpen, setReturnModalOpen] = useState(false);
    const [selectedLineItem, setSelectedLineItem] = useState(null);
    const [returns, setReturns] = useState([]);
    const { order, loading } = useSelector((state) => state.orderReducer);
    useEffect(() => {
        dispatch(fetchSingleOrder(id));
    }, [dispatch, id]);

    const handleReturnClick = (lineItem) => {
        setSelectedLineItem(lineItem);
        setReturnModalOpen(true);
    };

    useEffect(() => {
        const fetchReturns = async () => {
            const localOrderDetails = await fetchOrderByID(id);

            const parsedItems = localOrderDetails?.rows?.[0]?.order_details
                ? JSON.parse(localOrderDetails?.rows?.[0]?.order_details)
                : [];
            // Filter + Restructure into { [item_id]: line_item }
            const returnableItems = parsedItems
                .filter((item) => item.return_status)
                .reduce((acc, item) => {
                    acc[item.item_id] = item;
                    return acc;
                }, {});

            setReturns(returnableItems);
        };
        if (order) {
            fetchReturns();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    if (loading) {
        return <Spinner />;
    }
    return (
        <OrderSection>
            <Box>
                <Heading>Order Details</Heading>
                <Flex
                    p="4"
                    backgroundColor={'#FDFAE7'}
                    borderRadius="lg"
                    alignItems={'center'}
                    flexWrap={'wrap'}
                    justifyContent={'space-between'}>
                    <Box>
                        <Text fontWeight={'semibold'}>#{order?.salesorder?.salesorder_number}</Text>
                        <Flex mt="2" gap="2" fontSize={'sm'}>
                            <Text>{order?.salesorder?.line_items?.length} Products</Text>•
                            <Text>
                                Order Placed in{' '}
                                {moment(order?.salesorder?.created_at).format(
                                    'DD MMM, YYYY @ h:mm a',
                                )}
                            </Text>
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontWeight={'bold'} fontSize={'xl'}>
                            <WalletHistoryPrice price={order?.salesorder?.total} />
                        </Text>
                    </Box>
                </Flex>
            </Box>
            <Heading as={'h3'} fontSize={'lg'} fontWeight={'semibold'} mt="4">
                Order Items ({order?.salesorder?.line_items?.length})
            </Heading>
            <VStack alignItems="start" width={'100%'}>
                <Box>
                    <TableContainer>
                        <Table variant="simple" size="md">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        py={4}>
                                        Products
                                    </Th>
                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        py={4}>
                                        Price
                                    </Th>
                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        py={4}>
                                        Quantity
                                    </Th>
                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        py={4}>
                                        Sub-Total
                                    </Th>

                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        hidden={isEmpty(returns)}
                                        py={4}>
                                        Return Quantity
                                    </Th>
                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        hidden={isEmpty(returns)}
                                        py={4}>
                                        Return Status
                                    </Th>

                                    <Th
                                        color="gray.600"
                                        fontWeight="600"
                                        fontSize="sm"
                                        textTransform="uppercase"
                                        letterSpacing="wide"
                                        textAlign="center"
                                        py={4}>
                                        Action
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {order?.salesorder?.line_items?.map((product) => (
                                    <Tr
                                        key={product?.line_item_id}
                                        borderBottom="1px"
                                        borderColor="gray.200">
                                        <Td py={6}>
                                            <HStack spacing={4} align="start">
                                                <VStack align="start" spacing={1}>
                                                    <Text
                                                        fontWeight="600"
                                                        fontSize="sm"
                                                        color="gray.800">
                                                        {product.brand}
                                                    </Text>
                                                    <Text
                                                        fontSize="sm"
                                                        color="gray.600"
                                                        maxW={'200px'}
                                                        whiteSpace={'wrap'}>
                                                        {product.name}
                                                    </Text>
                                                </VStack>
                                            </HStack>
                                        </Td>
                                        <Td textAlign="center" py={6}>
                                            <Text fontWeight="600" fontSize="md" color="gray.800">
                                                <WalletHistoryPrice price={product.rate} />
                                            </Text>
                                        </Td>
                                        <Td textAlign="center" py={6}>
                                            <Text fontWeight="600" fontSize="md" color="gray.800">
                                                {product.quantity}
                                            </Text>
                                        </Td>
                                        <Td textAlign="center" py={6}>
                                            <Text fontWeight="700" fontSize="md" color="gray.900">
                                                <WalletHistoryPrice
                                                    price={product.item_sub_total}
                                                />
                                            </Text>
                                        </Td>
                                        <Td
                                            textAlign="center"
                                            py={6}
                                            hidden={!returns[product.item_id]}>
                                            <Text fontWeight="700" fontSize="md" color="gray.900">
                                                {returns[product.item_id]?.return_quantity}
                                            </Text>
                                        </Td>
                                        <Td
                                            textAlign="center"
                                            py={6}
                                            hidden={!returns[product.item_id]}>
                                            <Text fontWeight="700" fontSize="md" color="gray.900">
                                                {returns[product.item_id]?.return_status}
                                            </Text>
                                        </Td>
                                        <Td textAlign="center" py={6}>
                                            <Text fontWeight="700" fontSize="md" color="gray.900">
                                                {product.is_returnable &&
                                                product.quantity_returned < product.quantity &&
                                                checkReturnable(
                                                    order?.salesorder?.date,
                                                    order?.salesorder?.status,
                                                ) ? (
                                                    <Text
                                                        as={'button'}
                                                        textDecoration={'underline'}
                                                        color={'#1b3b5f'}
                                                        fontWeight={'bold'}
                                                        fontSize={'sm'}
                                                        _hover={{
                                                            textDecoration: 'none',
                                                            color: '#00f',
                                                        }}
                                                        onClick={() => handleReturnClick(product)}>
                                                        Return
                                                    </Text>
                                                ) : null}
                                            </Text>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {/* Billing Address Section */}
                    <Box mt={8} mb={4}>
                        <Divider mb={6} />
                        <Text fontSize="lg" fontWeight="600" color="gray.800" mb={4}>
                            Billing Address
                        </Text>
                        <VStack align="start" spacing={2} pl={0}>
                            <Text fontWeight="600" fontSize="md" color="gray.800">
                                {order?.salesorder?.customer_name}{' '}
                            </Text>
                            <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                                {order?.salesorder?.billing_address.address}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                                <Text as="span" fontWeight="500">
                                    Phone Number:
                                </Text>{' '}
                                {order?.salesorder?.contact_person_details?.[0]?.phone}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                                <Text as="span" fontWeight="500">
                                    Email:
                                </Text>{' '}
                                {order?.salesorder?.contact_person_details?.[0]?.email}
                            </Text>
                        </VStack>
                    </Box>
                </Box>
            </VStack>
            <SingleProductReturnModal
                isOpen={returnModalOpen}
                onClose={() => {
                    setReturnModalOpen(false);
                    setSelectedLineItem(null);
                }}
                lineItem={selectedLineItem}
                orderId={order?.salesorder.salesorder_id}
                orderRef={order?.salesorder.salesorder_number}
                isSubmitting={initiateProductReturn.isPending}
            />
        </OrderSection>
    );
};

export default OrderDetail;
